import { Check, MoreHoriz } from "@mui/icons-material";
import { Box, type SxProps, type Theme } from "@mui/material";
import { type FC, type ReactNode, type Ref, useState } from "react";
import { ICON_SIZE } from "../../../theme/tokens";
import { JumpingDots } from "../Animations/JumpingDots";
import { StatusChecklistDensity, type StatusChecklistItem, SystemStatusChecklist } from "../molecules/SystemStatusChecklist";
import { CollapsibleSection, CollapsibleSectionVariant } from "../Panels/CollapsibleSection";
import { ChatCard } from "./ChatCard";
import { type ChatWorkStep, ChatWorkStepState, ChatWorkStepsMode } from "./chatWorkStepTypes";

type CommonProps = {
	steps: readonly ChatWorkStep[];
	/** Accessible name for the list of steps. */
	ariaLabel: string;
	dataTestId?: string;
	sx?: SxProps<Theme>;
};

type LiveProps = CommonProps & {
	mode: ChatWorkStepsMode.Live;
	/** Decorative figure before the list (the assistant's thinking avatar); `aria-hidden`. */
	// DEFAULT: absent = the list alone, with moving dots on the active row.
	leading?: ReactNode;
	/** Width (px) of the leading column, so it lines up with the conversation's avatar column. */
	// DEFAULT: absent sizes the column to the leading node.
	leadingWidth?: number;
	rootRef?: Ref<HTMLDivElement>;
};

type SummaryProps = CommonProps & {
	mode: ChatWorkStepsMode.Summary;
	/** Already-translated disclosure line, e.g. "Checked 3 steps". */
	summaryLabel: string;
	onToggle: (isOpen: boolean) => void;
};

export type ChatWorkStepsProps = LiveProps | SummaryProps;

const STEP_ICON: Record<ChatWorkStepState, ReactNode> = {
	[ChatWorkStepState.Done]: <Check />,
	[ChatWorkStepState.Active]: <MoreHoriz />,
};

/** Small enough that the three dots fit the checklist's compact glyph column. */
const ACTIVE_DOT_SIZE_PX = 3;

// A leading figure already moves while it thinks, so the active row holds still beside it.
const activeGlyph = (hasLeading: boolean): ReactNode =>
	hasLeading ? (
		STEP_ICON[ChatWorkStepState.Active]
	) : (
		<Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", width: ICON_SIZE.MICRO, height: ICON_SIZE.MICRO }}>
			<JumpingDots size={ACTIVE_DOT_SIZE_PX} />
		</Box>
	);

const toItems = (steps: readonly ChatWorkStep[], hasLeading: boolean, dataTestId: string | undefined): StatusChecklistItem[] =>
	steps.map((step, index) => ({
		id: step.id,
		label: step.label,
		state: step.state,
		icon: step.state === ChatWorkStepState.Active ? activeGlyph(hasLeading) : STEP_ICON[step.state],
		dataTestId: dataTestId === undefined ? undefined : `${dataTestId}-row-${index}`,
	}));

/** What an assistant did for one turn: a live list of steps, or a finished turn's collapsible summary. */
export const ChatWorkSteps: FC<ChatWorkStepsProps> = (props) => {
	const [open, setOpen] = useState(false);
	const { steps, ariaLabel, dataTestId, sx } = props;

	if (props.mode === ChatWorkStepsMode.Summary) {
		return (
			<Box data-testid={dataTestId} sx={sx}>
				<CollapsibleSection
					variant={CollapsibleSectionVariant.Inline}
					title={props.summaryLabel}
					icon={<Check sx={{ color: "success.main" }} />}
					open={open}
					onToggle={() => {
						props.onToggle(!open);
						setOpen(!open);
					}}
					dataTestId={dataTestId === undefined ? undefined : `${dataTestId}-toggle`}
				>
					<SystemStatusChecklist
						items={toItems(steps, true, dataTestId)}
						ariaLabel={ariaLabel}
						density={StatusChecklistDensity.Compact}
						stagger={false}
						sx={{ pt: 0.5, pb: 0.75 }}
					/>
				</CollapsibleSection>
			</Box>
		);
	}

	const hasLeading = props.leading !== undefined;
	return (
		<Box
			ref={props.rootRef}
			data-testid={dataTestId}
			sx={[{ display: "flex", alignItems: "flex-start", gap: 1, width: "100%" }, ...(Array.isArray(sx) ? sx : [sx])]}
		>
			{hasLeading && (
				<Box aria-hidden sx={{ display: "flex", flexShrink: 0, width: props.leadingWidth }}>
					{props.leading}
				</Box>
			)}
			<ChatCard sx={{ width: "fit-content", maxWidth: "100%", px: 1.75, py: 1.25 }}>
				<SystemStatusChecklist items={toItems(steps, hasLeading, dataTestId)} ariaLabel={ariaLabel} density={StatusChecklistDensity.Compact} stagger={false} />
			</ChatCard>
		</Box>
	);
};
